import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  ArrowUpRight,
  Bell,
  Target,
  Clock,
  LineChart,
} from 'lucide-react';
import BlurText from '../components/BlurText';
import FadingVideo from '../components/FadingVideo';
import { STATIC_COUNTRY_LINKS, DEFAULT_LINK } from '../lib/countryLinks';
import { trackPageView } from '../lib/tracker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const SERIF = { fontFamily: "'Nacelle', -apple-system, sans-serif", fontWeight: 600 };
const SANS = { fontFamily: "'Nacelle', -apple-system, sans-serif", fontWeight: 400 };

const fadeUp = (delay = 0) => ({
  initial: { filter: 'blur(10px)', opacity: 0, y: 20 },
  whileInView: { filter: 'blur(0px)', opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.8, ease: 'easeOut', delay },
});

const TRUST_ITEMS = [
  'Dibina untuk trader',
  'Alert realtime',
  'Pair · Timeframe',
  'No noise, only valid zones',
  '5,000+ active members',
  'Akses dari mana saja',
];

const BENEFITS = [
  { icon: Bell, title: 'Alert Real Time', desc: 'Telegram bunyi sebaik zon MBE terbentuk. Tak lambat walau satu candle pun.' },
  { icon: Target, title: 'Pair Disertakan', desc: 'Setiap alert bagitahu pair mana tengah trigger. Tak payah teka.' },
  { icon: Clock, title: 'Timeframe + Entry', desc: 'M15, M30, H1 dengan entry price jelas dalam satu pandangan.' },
  { icon: LineChart, title: 'Untuk Trader Sibuk', desc: 'Tak perlu depan chart. Hidupkan hidup anda dan biar alert panggil bila zon dah sah.' },
];

const TESTIMONIAL_CARDS = [
  {
    image: 'https://customer-assets-0z36b82j.emergentagent.net/job_theme-17/artifacts/tlp113zl_photo_6059656860308018018_y.jpg',
    objectPos: '50% 32%',
    category: 'RESULT',
    title: '+$10,933 Caught',
    desc: 'Member Kingsley scalp XAUUSD ikut alert dan sasar 5% growth harian yang konsisten.',
    cta: 'Real Result',
  },
  {
    image: 'https://customer-assets-0z36b82j.emergentagent.net/job_theme-17/artifacts/sgqt2opt_photo_6059656860308018017_y.jpg',
    objectPos: '50% 62%',
    category: 'PROOF',
    title: '$1K to $6K, 5 Minggu',
    desc: 'Mehmet ikut sistem MBE untuk scalp XAUUSD dan grow account dalam masa singkat.',
    cta: 'View Proof',
  },
  {
    image: 'https://customer-assets-0z36b82j.emergentagent.net/job_theme-17/artifacts/p5o6vx8e_photo_6059656860308018021_y.jpg',
    objectPos: '50% 40%',
    category: 'FEEDBACK',
    title: 'Scalp Post CPI',
    desc: 'Syam tunggu signal MBE untuk scalp XAUUSD selepas news impact. Bagus untuk hari volatile.',
    cta: 'Read Story',
  },
  {
    image: 'https://customer-assets-0z36b82j.emergentagent.net/job_theme-17/artifacts/qltmk93o_photo_6059656860308018024_y.jpg',
    objectPos: '50% 38%',
    category: 'FEEDBACK',
    title: 'Withdraw Harian',
    desc: 'Member disiplin dengan small account dan withdraw setiap hari. Konsisten lebih penting dari lot besar.',
    cta: 'Member Wins',
  },
];

const MalayLanding = () => {
  const [link, setLink] = useState(STATIC_COUNTRY_LINKS.MY || DEFAULT_LINK);

  useEffect(() => {
    try {
      window.__mbeCountry = 'MY';
      window.__mbeCountrySource = 'route';
    } catch (e) { /* noop */ }
    trackPageView({ country: 'MY', source: 'route' });

    // Fetch dynamic MY link from admin-configured countries
    fetch(`${BACKEND_URL}/api/country-links`)
      .then((r) => (r.ok ? r.json() : {}))
      .then((map) => {
        if (map && map.MY) setLink(map.MY);
      })
      .catch(() => {});
  }, []);

  const CTA = ({ children, size = 'md', className = '' }) => (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-flex items-center gap-2 bg-[#FF6E00] hover:bg-[#FF8533] text-white transition-colors shadow-[0_20px_45px_-15px_rgba(255,110,0,0.7)] ${
        size === 'lg' ? 'text-[14px] sm:text-[15px] px-6 py-3.5 sm:px-7 sm:py-4' : 'text-[12.5px] sm:text-[14px] px-4 py-2.5 sm:px-5 sm:py-3'
      } rounded-full ${className}`}
      style={SANS}
    >
      {children} <ArrowUpRight className="w-4 h-4 shrink-0" />
    </a>
  );

  return (
    <div className="bg-black text-white min-h-screen" style={SANS}>
      {/* Navbar */}
      <header className="fixed top-3 sm:top-4 left-0 right-0 z-50 px-4 sm:px-8 lg:px-16 flex items-center justify-between">
        <a href="/my" aria-label="MBE International" className="flex items-center">
          <img
            src="https://customer-assets-0z36b82j.emergentagent.net/job_theme-17/artifacts/rhw1my68_PROFILE%20MBE%20INTERNATIONAL%20OREN%202%20%281%29.webp"
            alt="MBE International"
            className="h-10 sm:h-12 w-auto"
            width="180"
            height="48"
            fetchPriority="high"
          />
        </a>
        <CTA size="md" className="!px-3.5 sm:!px-5">
          <span className="hidden sm:inline">Get MBE Alert Now</span>
          <span className="inline sm:hidden">Get Access</span>
        </CTA>
      </header>

      {/* HERO */}
      <section className="relative min-h-screen w-full overflow-hidden pt-24 pb-0 flex flex-col">
        <FadingVideo
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260418_080021_d598092b-c4c2-4e53-8e46-94cf9064cd50.mp4"
          className="absolute left-1/2 top-0 -translate-x-1/2 object-cover object-top z-0"
          style={{ width: '120%', height: '120%' }}
        />
        <div
          className="absolute inset-0 z-[1] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 70%, rgba(255,110,0,0.12) 0%, rgba(255,110,0,0) 55%)' }}
        />

        <div className="relative z-10 flex-1 flex items-center justify-center">
          <div className="max-w-4xl mx-auto px-5 text-center pt-8 pb-16 sm:pt-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="inline-flex items-center gap-2 pl-1 py-1 pr-3 rounded-full border border-[#FF8533]/30 bg-[#FF8533]/[0.08]"
            >
              <span className="bg-white text-black px-2.5 py-0.5 text-[10.5px] font-semibold rounded-full">Live</span>
              <span className="text-[12px] sm:text-sm text-white/90">Telegram Alerts · MBE System</span>
            </motion.div>

            <div className="mt-6 sm:mt-7" style={SERIF}>
              <BlurText
                text="AI Signal Direct Ke Phone Anda"
                highlightWords={['Phone', 'Anda']}
                highlightClassName="text-[#FF6E00]"
                className="text-[2rem] sm:text-5xl md:text-6xl lg:text-[4.75rem] text-white leading-[1] sm:leading-[0.95] tracking-[-1px] sm:tracking-[-2px]"
              />
            </div>

            <motion.p
              {...fadeUp(0.7)}
              className="mt-5 sm:mt-6 text-white/85 text-[14px] sm:text-[16px] max-w-2xl mx-auto leading-relaxed"
            >
              MBE Alert terus bagi notification direct ke Telegram anda sebaik je price masuk zon. Tak perlu susah-susah menghadap screen sepanjang hari.
            </motion.p>
            <motion.p
              {...fadeUp(0.9)}
              className="mt-3 text-[#FFA66B] text-[13px] sm:text-[15px]"
              style={SERIF}
            >
              Dapat Alert → Entry → Collect Profit
            </motion.p>

            <motion.div {...fadeUp(1.1)} className="mt-7 sm:mt-8">
              <CTA size="lg">Get MBE Alert Now</CTA>
            </motion.div>
          </div>
        </div>

        {/* Trust bar — pinned at absolute bottom of hero */}
        <div className="relative z-10 mt-auto border-y border-white/8 bg-black/40 overflow-hidden">
          <div className="flex gap-10 py-3.5 animate-[marquee_35s_linear_infinite] whitespace-nowrap">
            {[...TRUST_ITEMS, ...TRUST_ITEMS, ...TRUST_ITEMS].map((t, i) => (
              <div key={i} className="flex items-center gap-10">
                <span className="text-white/55 uppercase tracking-[0.22em] text-[11px]">{t}</span>
                <span className="text-[#FF8533]/60">/</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS — Secret Tool 4-feature grid */}
      <section className="py-16 sm:py-24 lg:py-28 px-5 lg:px-8 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(255,110,0,0.10) 0%, rgba(255,110,0,0) 50%)' }}
        />

        <div className="relative max-w-6xl mx-auto">
          {/* Heading */}
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              {...fadeUp(0)}
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#FF8533]/25 bg-[#FF8533]/[0.05] text-[#FFA66B] text-[11px] tracking-[0.2em] font-medium uppercase"
            >
              Secret Tool
            </motion.div>
            <motion.h2
              {...fadeUp(0.1)}
              className="mt-5 sm:mt-6 text-[2rem] sm:text-4xl md:text-5xl lg:text-6xl text-white tracking-[-1px] sm:tracking-[-1.5px] leading-[1.05] sm:leading-[1]"
              style={SERIF}
            >
              Secret Tool Yang <span className="text-[#FF8533]">Wajib Trader Guna.</span>
            </motion.h2>
            <motion.p
              {...fadeUp(0.2)}
              className="mt-5 text-white/60 text-[15px] max-w-xl mx-auto font-light"
            >
              Empat mekanik. Zero noise. Dibina keliling sistem MBE.
            </motion.p>
          </div>

          {/* Image + 4-feature grid */}
          <div className="mt-12 sm:mt-14 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            <motion.div {...fadeUp(0.1)} className="lg:col-span-5 relative order-1">
              <div className="absolute inset-0 bg-gradient-to-br from-[#FF6E00]/25 via-[#FF8533]/12 to-transparent blur-3xl rounded-full pointer-events-none" />
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_theme-17/artifacts/gy1430cm_Gemini_Generated_Image_76x15q76x15q76x1.png"
                  alt="MBE Alert delivered to your phone via Telegram"
                  className="w-full max-w-md mx-auto block"
                  width="900"
                  height="900"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="relative mt-4 text-center">
                <div className="text-white text-[20px] sm:text-[24px] tracking-[-0.5px] leading-tight" style={SERIF}>
                  Alert sebenar. Terus ke telefon anda.
                </div>
                <p className="mt-2 text-white/55 text-[13px] sm:text-[13.5px] max-w-sm mx-auto">
                  Pair, price, timeframe, SL, TP semua tersusun rapi ke Telegram anda sebaik zon dah sah.
                </p>
              </div>
            </motion.div>

            <div className="lg:col-span-7 grid grid-cols-2 gap-3 sm:gap-4 order-2">
              {BENEFITS.map((b, i) => (
                <motion.div
                  key={i}
                  {...fadeUp(i * 0.08)}
                  className="group bg-white/[0.025] border border-white/8 rounded-2xl p-4 sm:p-6 hover:border-[#FF8533]/30 hover:bg-white/[0.045] transition-all duration-300"
                >
                  <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#FF6E00]/15 border border-[#FF8533]/25 flex items-center justify-center mb-3 sm:mb-5 group-hover:bg-[#FF6E00]/25 transition-colors">
                    <b.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#FFA66B]" />
                  </div>
                  <h4 className="text-white text-[17px] sm:text-2xl tracking-[-0.5px] leading-tight" style={SERIF}>
                    {b.title}
                  </h4>
                  <p className="mt-1.5 sm:mt-2 text-white/55 text-[12.5px] sm:text-[13.5px] leading-relaxed font-light">
                    {b.desc}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="text-center mt-14">
            <CTA size="lg">Get MBE Alert Now</CTA>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — Auto-moving screenshot carousel */}
      <section className="py-16 sm:py-24 lg:py-28 relative overflow-hidden bg-black">
        <div className="max-w-5xl mx-auto text-center px-5">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#FF8533]/25 bg-[#FF8533]/[0.06] text-[#FFA66B] text-[11px] tracking-[0.2em] uppercase">
            Feedback Trader Sebenar
          </div>
          <motion.h2
            {...fadeUp(0.1)}
            className="mt-5 sm:mt-6 text-[1.85rem] sm:text-4xl md:text-5xl text-white tracking-[-1px] sm:tracking-[-1.5px] leading-[1.1]"
            style={SERIF}
          >
            Monitor chart tak sampai 5 minit sehari, <span className="text-[#FF6E00]">boleh jadi profitable trader.</span>
          </motion.h2>
          <p className="mt-4 sm:mt-5 text-white/60 text-[13.5px] sm:text-[15px] max-w-xl mx-auto">
            Screenshot sebenar dari members MBE Alert. Semua feedback tanpa suruh.
          </p>
        </div>

        {/* Marquee card carousel — pause on hover */}
        <div className="mt-10 sm:mt-14 relative group">
          {/* Edge fade masks */}
          <div className="pointer-events-none absolute inset-y-0 left-0 w-16 sm:w-40 z-10 bg-gradient-to-r from-black to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-16 sm:w-40 z-10 bg-gradient-to-l from-black to-transparent" />

          <div
            className="flex gap-4 sm:gap-6 py-2 animate-[marquee_25s_linear_infinite] whitespace-nowrap will-change-transform group-hover:[animation-play-state:paused] [&>*]:whitespace-normal"
          >
            {[...TESTIMONIAL_CARDS, ...TESTIMONIAL_CARDS].map((t, i) => (
              <article
                key={i}
                className="shrink-0 w-[260px] sm:w-[320px] rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a0a] shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)] hover:border-[#FF8533]/40 transition-colors flex flex-col"
              >
                {/* Cropped profit section only */}
                <div className="relative w-full aspect-[4/5] bg-black overflow-hidden">
                  <img
                    src={t.image}
                    alt={`Feedback trader ${t.title}`}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ objectPosition: t.objectPos }}
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                </div>
                {/* Footer content */}
                <div className="p-5 sm:p-6 bg-[#0a0a0a] flex flex-col gap-2.5 flex-1">
                  <div className="text-[#FFA66B] text-[10.5px] tracking-[0.24em] font-medium">
                    {t.category}
                  </div>
                  <h3 className="text-white text-[19px] sm:text-[22px] leading-tight tracking-[-0.5px]" style={SERIF}>
                    {t.title}
                  </h3>
                  <p className="text-white/60 text-[13px] sm:text-[13.5px] leading-relaxed">
                    {t.desc}
                  </p>
                  <div className="mt-2">
                    <span className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#FF6E00]/12 border border-[#FF8533]/35 text-[#FFA66B] text-[11.5px] sm:text-[12px] font-medium">
                      {t.cta}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        <p className="text-center mt-8 text-white/40 text-[12px] px-5">
          Setiap trader boleh dapat hasil berbeza. Trading ada risiko. Sila manage risk anda dengan betul.
        </p>

        <div className="text-center mt-14 px-5">
          <CTA size="lg">Get MBE Alert Now</CTA>
        </div>
      </section>

      {/* FINAL PUSH */}
      <section className="py-16 sm:py-20 lg:py-24 px-5 relative overflow-hidden">
        <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-br from-[#FF6E00]/[0.15] to-[#FF8533]/[0.04] border border-[#FF8533]/30 p-7 sm:p-10 lg:p-14 text-center relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-[#FF6E00]/15 blur-[100px] pointer-events-none" />
          <div className="relative">
            <h3
              className="text-white text-[26px] sm:text-[40px] lg:text-[52px] tracking-[-1.5px] sm:tracking-[-2px] leading-[1.02] sm:leading-[0.98]"
              style={SERIF}
            >
              Trader yang react <span className="text-[#FF6E00]">on time</span>
              <br />sentiasa satu langkah <span className="text-[#FF6E00]">di depan.</span>
            </h3>
            <p className="mt-4 sm:mt-5 text-white/70 text-[13.5px] sm:text-[15px] max-w-xl mx-auto">
              MBE Alert bagi anda timing yang tepat. Anda decide, anda execute. Simple.
            </p>
            <div className="mt-7 sm:mt-8">
              <CTA size="lg">Get MBE Alert Now</CTA>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/8 py-10 sm:py-12 px-5">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="text-white text-xl sm:text-2xl tracking-[-0.5px]" style={SERIF}>
              MBE <span className="text-[#FF6E00]">Alert.</span>
            </div>
            <p className="mt-2 sm:mt-3 text-white/45 text-[12.5px] sm:text-[13px] max-w-md leading-relaxed">
              Real time Telegram alerts untuk zon MBE yang sah. Dibina untuk trader Malaysia yang sibuk.
            </p>
          </div>
          <CTA>Get MBE Alert Now</CTA>
        </div>
        <div className="max-w-6xl mx-auto mt-6 sm:mt-8 pt-5 sm:pt-6 border-t border-white/5 text-white/30 text-[11px] sm:text-[11.5px] leading-relaxed">
          © {new Date().getFullYear()} MBE Alert. Trading involves risk. Past performance does not guarantee future results.
        </div>
      </footer>
    </div>
  );
};

export default MalayLanding;
